import { useState } from "react"

import { useQueryExportGroupStoreTransactionsTask } from "api/reactQuery/queries/export"
import { ExportType } from "api/reactQuery/queries/export.types"
import { useQueryGroups } from "api/reactQuery/queries/groups"
import { useExportGuard, useExportTasks } from "hooks/exportReports"
import { useCustomSnackbar } from "hooks/snackbar"
import { useTranslation } from "react-i18next"
import { getErrorMessageFromTab } from "utils/api"

interface IGroupStoreExportOptions {
  enabled: boolean
}

export const useGroupStoreExport = (
  classId: string,
  { enabled }: IGroupStoreExportOptions,
) => {
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const [isExportData, setIsExportData] = useState(false)
  // null = all groups, so no group_ids is sent
  const [selectedIds, setSelectedIds] = useState<number[] | null>(null)

  const { data: groupsData } = useQueryGroups({
    classId: Number(classId),
    options: { enabled: enabled && !!classId },
  })
  const groups = groupsData?.data ?? []
  const hasGroups = groups.length > 0
  const isNoGroupSelected = selectedIds?.length === 0

  const {
    successTasks,
    pendingTasks,
    setExportReportIdToDownload,
    handleExportSuccess,
    refreshTasks,
    removeTask,
  } = useExportTasks({
    exportType: ExportType.GROUP_STORE_TRANSACTIONS,
    classId: Number(classId),
  })

  const { isFetching } = useQueryExportGroupStoreTransactionsTask({
    classId: Number(classId),
    group_ids:
      selectedIds && selectedIds.length < groups.length
        ? selectedIds
        : undefined,
    options: {
      enabled: isExportData,
      staleTime: 0,
      onSuccess: (response) => {
        setIsExportData(false)
        handleExportSuccess(response, Number(classId))
        showSnackbar({
          title: t("export.fileIsCurrentlyBeingGenerated"),
        })
      },
      onError: (err) => {
        setIsExportData(false)
        showSnackbar({
          title: t(getErrorMessageFromTab(err)),
          variant: "error",
        })
      },
    },
  })

  const { isGenerateDisabled: hasPendingExport } = useExportGuard({
    hasPendingTasks: pendingTasks.length > 0,
  })

  const isGenerateDisabled = !hasGroups || isNoGroupSelected || hasPendingExport

  const generateDisabledTooltip = !hasGroups
    ? t("group.exportStoreByGroupNoGroups")
    : isNoGroupSelected
      ? t("group.exportStoreByGroupNoneSelected")
      : t("export.reportCurrentlyBeingGenerated")

  const requestExport = () => setIsExportData(true)

  const reset = () => setSelectedIds(null)

  return {
    groups,
    hasGroups,
    selectedIds,
    setSelectedIds,
    successTasks,
    pendingTasks,
    isFetching,
    refreshTasks,
    removeTask,
    setExportReportIdToDownload,
    requestExport,
    isGenerateDisabled,
    generateDisabledTooltip,
    reset,
  }
}
